import { useSuspenseQueries } from '@tanstack/react-query';
import type * as React from 'react';
import { type StyleProp, View, type ViewStyle } from 'react-native';

import type * as api from '@gitbook/api-client';
import { BackgroundView, sizes, ThemedStyleSheet, useCurrentTheme } from '@gitbook/react-uikit';
import { AvatarSizeFromPixelValue } from '@gitbook/spine-react';
import { filterOutNullable } from '@gitbook/util-ts';

import { apiQuery } from '../../state';
import { UserAvatar } from './UserAvatar';

export type UsersGroupAvatarProps = {
    /** If only one user, should it also be displayed as a stack? */
    alwaysAsGroup?: boolean;
    size?: number;
};

/**
 * Multiple user avatars displayed in a single square.
 */
export function UsersGroupAvatar(
    props: {
        users: api.User[];
    } & UsersGroupAvatarProps
): React.ReactElement {
    const { users, size = sizes.ICON_400, alwaysAsGroup = true } = props;

    const first = users[0];
    const second = users[1];

    if (!second && !alwaysAsGroup) {
        return first ? (
            <UserAvatar user={first} size={AvatarSizeFromPixelValue[size]} />
        ) : (
            <UserAvatarPlaceholder size={size} />
        );
    }

    return (
        <UsersGroupAvatarPlaceholder
            size={size}
            first={
                first ? (
                    <UserAvatar user={first} size={AvatarSizeFromPixelValue[size / 2]} />
                ) : undefined
            }
            second={
                second ? (
                    <UserAvatar user={second} size={AvatarSizeFromPixelValue[size / 2]} />
                ) : undefined
            }
        />
    );
}

/**
 * Multiple user avatars displayed in a single square.
 */
export function UsersGroupAvatarByIds(
    props: {
        userIds: string[];
    } & UsersGroupAvatarProps
): React.ReactElement {
    const { userIds, ...rest } = props;
    const users = useSuspenseQueries({
        queries: userIds
            .slice(0, 2)
            .map((userId) => apiQuery('getUserById', { userId }, { nullOnError: true })),
    });

    return (
        <UsersGroupAvatar
            {...rest}
            users={users.map(({ data }) => data).filter(filterOutNullable)}
        />
    );
}

/**
 * Placeholder for a group of avatars. It can be used as a suspense fallback.
 */
export function UsersGroupAvatarPlaceholder(props: {
    size?: number;
    first?: React.ReactNode;
    second?: React.ReactNode;
}): React.ReactElement {
    const [, styles] = useCurrentTheme(themedStyles);

    const {
        size = 18,
        first = <UserAvatarPlaceholder size={size / 1.5} />,
        second = <UserAvatarPlaceholder size={size / 1.5} />,
    } = props;

    return (
        <View
            style={[
                styles.usersGroupAvatar,
                {
                    width: size,
                    height: size,
                },
            ]}
        >
            <View style={[styles.firstUserGroupAvatar, { borderRadius: size / 1.5 }]}>
                {second}
            </View>
            <View style={[styles.secondUserGroupAvatar, { borderRadius: size / 1.5 }]}>
                {first}
            </View>
        </View>
    );
}

/**
 * Placeholder for a user avatar.
 */
export function UserAvatarPlaceholder(props: {
    size: number;
    style?: StyleProp<ViewStyle>;
}): React.ReactElement {
    const { size, style } = props;
    const [theme] = useCurrentTheme();
    return (
        <BackgroundView
            style={[
                {
                    borderRadius: size,
                    width: size,
                    height: size,
                },
                style,
            ]}
            backgroundColor={theme.colors.background.muted}
        />
    );
}

const themedStyles = ThemedStyleSheet.create({
    usersGroupAvatar: {
        position: 'relative',
    },
    firstUserGroupAvatar: (theme) => ({
        position: 'absolute',
        top: 0,
        right: 0,
        borderWidth: 1,
        borderColor: theme.colors.background.main,
    }),
    secondUserGroupAvatar: (theme) => ({
        position: 'absolute',
        bottom: 0,
        left: 0,
        borderWidth: 1,
        borderColor: theme.colors.background.main,
    }),
});
